import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, color, motion } from 'framer-motion';
import { useKeyboard } from '../Keyboard';
import getIcon from '../../utils/Icon';
import CrystalsInActive from '../icons/CrystalsInActive';
import CCInActive from '../icons/CCInActive';
import CryptoInActive from '../icons/CryptoInActive';

const PromoGuideContext = createContext(null);

export const GUIDE_STEPS = [
    'prizePool',
    'categories',
    'players',
    'winners',
    'tierWinners',
    'tierBudget',
    'telegramLink',
    'promoTime',
    'addTasks',
];

const NEXT_STEP = {
    prizePool: 'categories',
    categories: 'players',
    players: 'winners',
    winners: 'tierWinners',
    tierWinners: 'tierBudget',
    tierBudget: 'telegramLink',
    telegramLink: 'promoTime',
    promoTime: 'addTasks',
    addTasks: null,
};

export function GuideStepCounter({ className = '' }) {
    const { stepNumber, totalSteps } = usePromoGuide();
    if (!stepNumber) return null;
    return (
        <span className={`text-white/70 text-[12px] leading-none ${className}`}>
            {stepNumber}/{totalSteps}
        </span>
    );
}

/** Skip whole onboarding — left side; hidden while keyboard is open */
export function GuideSkipButton() {
    const { step, skipGuide } = usePromoGuide();
    const { isOpen, closeKeyboard } = useKeyboard();
    if (!step || isOpen || typeof document === 'undefined') return null;

    return createPortal(
        <button
            type="button"
            onClick={() => {
                skipGuide();
                if (isOpen) closeKeyboard({ applied: false });
            }}
            className="fixed bottom-[140px] right-4 z-[9999] h-[32px] px-3 rounded-[8px] bg-[#F59E0B] border border-[#F59E0B]
                text-black text-[13px] font-semibold whitespace-nowrap active:scale-[0.97] transition-transform"
        >
            Skip the Guide
        </button>,
        document.body
    );
}

const introBannerMotion = {
    initial: { opacity: 0, y: -14 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 },
    transition: { duration: 0.3, ease: [0.32, 0.72, 0, 1] },
};


function StatusTile({ label, iconSrc, icon, enabled }) {
    return (
        <div className="flex flex-col items-center gap-[4px] min-w-[48px] ">
            <div className="relative">
                <div
                    className={`w-[34px] h-[34px] rounded-[8px] flex items-center justify-center border border-[#353232] ${
                        enabled ? 'bg-[#1A1A1A]' : 'bg-[#1A1A1A]'
                    }`}
                >
                    {icon || (
                        <img
                            src={iconSrc}
                            alt=""
                            className="w-[18px] h-[18px] object-contain brightness-0 invert"
                        />
                    )}
                </div>
                <div
                    className={`absolute -top-[5px] -left-[5px] w-[15px] h-[15px] rounded-full flex items-center justify-center  bg-black`}
                >
                    <div className={`w-full h-full rounded-full flex items-center justify-center ${enabled ? 'bg-[#2FD159] '  : 'bg-[#EF4444]'} `}>
                        {enabled ? (
                            <svg width="9" height="9" viewBox="0 0 12 12" fill="none" aria-hidden>
                                <path
                                    d="M2.5 6.2L4.8 8.5L9.5 3.5"
                                    stroke="#FFFFFF"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        ) : (
                            <svg width="8" height="8" viewBox="0 0 12 12" fill="none" aria-hidden>
                                <path
                                    d="M3 3L9 9M9 3L3 9"
                                    stroke="#FFFFFF"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                />
                            </svg>
                        )}
                    </div>
                </div>
            </div>
            <span className="text-white text-[10px] leading-none font-medium">{label}</span>
        </div>
    );
}

function IntroBannerCard({
    title,
    lines = [],
    variant = 'onlyCrystals',
}) {
    const crystalsOn = variant !== 'cryptoOnly';
    const cryptoOn = variant !== 'onlyCrystals';
    const leadIcon =
        variant === 'cryptoOnly' ? getIcon('ETH') : getIcon('ou_diamond');
    const subtitle = lines.filter(Boolean).join(' ');

    return (
        <div className="w-full rounded-[14px] bg-[#1A1A1A] px-3 py-3 border border-[#353232] shadow-[0_8px_24px_rgba(0,0,0,0.35)]">
            <div className="flex items-center gap-2.5">
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    {/* <img
                        src={leadIcon}
                        alt=""
                        className="w-[28px] h-[28px] object-contain flex-shrink-0 brightness-0 invert opacity-90"
                    /> */}
                    {variant === 'crystalsAndCrypto' ? (
                        <CCInActive stroke="#EAEAEA" fill="#EAEAEA" />
                    ) : variant === 'cryptoOnly' ? (
                        <CryptoInActive />
                    ) : (
                        <CrystalsInActive stroke="#EAEAEA" />
                    )}
                    <div className="min-w-0 flex-1">
                        <p className="text-white text-[12px] font-semibold leading-[16px]">
                            {title}
                        </p>
                        {subtitle ? (
                            <p className="mt-0.5 text-white/70 text-[10px] leading-[14px]">
                                {subtitle}
                            </p>
                        ) : null}
                    </div>
                </div>

                

                <div className="flex items-center gap-3 flex-shrink-0 pl-0.5">
                    <StatusTile
                        label="Crystals"
                        icon={<CrystalsInActive width={18} height={20} stroke="#EAEAEA" />}
                        enabled={crystalsOn}
                    />
                    <StatusTile
                        label="Crypto"
                        iconSrc={getIcon('ETH')}
                        enabled={cryptoOn}
                    />
                </div>
            </div>
        </div>
    );
}

/**
 * Temporary top intro — appears on open, then fades out.
 * Used for Crypto&Crystals / Crystals (no multi-step guide).
 */
export function PromoTypeIntroBanner({
    enabled = true,
    title,
    lines = [],
    variant = 'onlyCrystals',
    durationMs = 2000,
}) {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (!enabled) {
            setVisible(false);
            return undefined;
        }
        setVisible(true);
        const timer = setTimeout(() => setVisible(false), durationMs);
        return () => clearTimeout(timer);
    }, [enabled, durationMs]);

    if (typeof document === 'undefined') return null;

    return createPortal(
        <AnimatePresence>
            {visible && (
                <motion.div
                    key="promo-type-intro"
                    {...introBannerMotion}
                    className="fixed top-[85px] left-0 right-0 z-[9998] flex justify-center px-4 pointer-events-none"
                >
                    <div className="w-full max-w-[360px]">
                        <IntroBannerCard title={title} lines={lines} variant={variant} />
                    </div>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    );
}

/** Top banner — only on Crypto only guide step 1 (prizePool) */
export function GuideIntroBanner() {
    const { step } = usePromoGuide();
    if (typeof document === 'undefined') return null;

    return createPortal(
        <AnimatePresence>
            {step === 'prizePool' && (
                <motion.div
                    key="guide-intro-banner"
                    {...introBannerMotion}
                    className="fixed top-[85px] left-0 right-0 z-[9998] flex justify-center px-4 pointer-events-none"
                >
                    <div className="w-full max-w-[360px]">
                        <IntroBannerCard
                            variant="cryptoOnly"
                            title={'This is "Crypto only" Promo type'}
                            lines={[
                                'You can customize your reward tiers and winners.',
                                'Follow the Guide.',
                            ]}
                        />
                    </div>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    );
}

/**
 * Advances the guide once the custom keyboard closes after it was opened
 * during this step (whether the user entered a value or not).
 */
export function useAdvanceGuideOnKeyboardClose(enabled, onAdvance) {
    const _enabled = enabled;
    const _onAdvance = onAdvance;
    // Guide step changes are manual via the Tour "Next" button only.
    // Keep hook signature for compatibility with existing callers.
    return [_enabled, _onAdvance];
}

export function PromoGuideProvider({ children, initialStep = null }) {
    const [step, setStep] = useState(initialStep);

    const advance = useCallback(() => {
        setStep((current) => (current ? NEXT_STEP[current] ?? null : null));
    }, []);

    const skipGuide = useCallback(() => {
        setStep(null);
    }, []);

    const completePrizePool = useCallback(() => {
        setStep((current) => (current === 'prizePool' ? 'categories' : current));
    }, []);

    const completeCategories = useCallback(() => {
        setStep((current) => (current === 'categories' ? 'players' : current));
    }, []);

    const completeBudget = useCallback(() => {
        // budget guide step removed — kept for API compatibility
    }, []);

    const completePlayers = useCallback(() => {
        setStep((current) => (current === 'players' ? 'winners' : current));
    }, []);

    const completeWinners = useCallback(() => {
        setStep((current) => (current === 'winners' ? 'tierWinners' : current));
    }, []);

    const completeTierWinners = useCallback(() => {
        setStep((current) => (current === 'tierWinners' ? 'tierBudget' : current));
    }, []);

    const completeTierBudget = useCallback(() => {
        setStep((current) => (current === 'tierBudget' ? 'telegramLink' : current));
    }, []);

    const completeTelegramLink = useCallback(() => {
        setStep((current) => (current === 'telegramLink' ? 'promoTime' : current));
    }, []);

    const completePromoTime = useCallback(() => {
        setStep((current) => (current === 'promoTime' ? 'addTasks' : current));
    }, []);

    const completeAddTasks = useCallback(() => {
        setStep(null);
    }, []);

    /** @deprecated backdrop must not advance */
    const dismiss = useCallback(() => {}, []);

    const stepNumber = step ? GUIDE_STEPS.indexOf(step) + 1 : 0;
    const totalSteps = GUIDE_STEPS.length;

    const value = useMemo(() => ({
        step,
        setStep,
        stepNumber,
        totalSteps,
        advance,
        skipGuide,
        completePrizePool,
        completeCategories,
        completeBudget,
        completePlayers,
        completeWinners,
        completeTierWinners,
        completeTierBudget,
        completeTelegramLink,
        completePromoTime,
        completeAddTasks,
        dismiss,
    }), [
        step,
        stepNumber,
        totalSteps,
        advance,
        skipGuide,
        completePrizePool,
        completeCategories,
        completeBudget,
        completePlayers,
        completeWinners,
        completeTierWinners,
        completeTierBudget,
        completeTelegramLink,
        completePromoTime,
        completeAddTasks,
        dismiss,
    ]);

    return (
        <PromoGuideContext.Provider value={value}>
            {children}
        </PromoGuideContext.Provider>
    );
}

export function usePromoGuide() {
    return useContext(PromoGuideContext) || {
        step: null,
        setStep: () => {},
        stepNumber: 0,
        totalSteps: GUIDE_STEPS.length,
        advance: () => {},
        skipGuide: () => {},
        completePrizePool: () => {},
        completeCategories: () => {},
        completeBudget: () => {},
        completePlayers: () => {},
        completeWinners: () => {},
        completeTierWinners: () => {},
        completeTierBudget: () => {},
        completeTelegramLink: () => {},
        completePromoTime: () => {},
        completeAddTasks: () => {},
        dismiss: () => {},
    };
}
